import { handleApiResult } from '@store/error';
import { apiSlice } from './apiSlice';

export interface User {
  id: string;
  name: string;
  username: string;
  role: 'sysadmin' | 'admin' | 'user';
  createdAt: string;
  updatedAt: string;
}

export const permissions = ['read', 'create', 'update', 'delete'] as const;

export type Permission = typeof permissions[number];

export interface ApiKey {
  _id: string;
  name: string;
  permissions: Permission[];
  createdAt: string;
  updatedAt: string;
}

export const userApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], void>({
      query: () => '/v1/users',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ id }) => ({ type: 'User' as const, id })),
            { type: 'User', id: 'LIST' },
          ]
          : [{ type: 'User', id: 'LIST' }],
      onQueryStarted: handleApiResult(),

    }),
    addUser: builder.mutation<void, Omit<User, 'id' | 'createdAt' | 'updatedAt'>>({
      query: (user) => ({
        url: '/v1/users',
        method: 'POST',
        body: user,
      }),
      invalidatesTags: ['User'],
      onQueryStarted: handleApiResult('Api_User_Add_Successful'),
    }),
    updateUser: builder.mutation<User, Partial<User> & Pick<User, 'id'>>({
      query: ({ id, ...rest }) => ({
        url: `/v1/users/${id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_result, _error, { id }) => [{ type: 'User' as const, id }],
      onQueryStarted: handleApiResult('Api_User_Update_Successful'),
    }),
    deleteUser: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/users/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'User' as const, id }],
      onQueryStarted: handleApiResult('Api_User_Delete_Successful'),
    }),
    getApiKeys: builder.query<ApiKey[], void>({
      query: () => '/v1/api-key',
      providesTags: (result) =>
        result
          ? [
            ...result.map(({ _id: id }) => ({ type: 'ApiKey' as const, id })),
            { type: 'ApiKey', id: 'LIST' },
          ]
          : [{ type: 'ApiKey', id: 'LIST' }],
      onQueryStarted: handleApiResult(),
    }),
    addApiKey: builder.mutation<{ apiKey: string }, Omit<ApiKey, '_id' | 'createdAt' | 'updatedAt'>>({
      query: (apiKey) => ({
        url: '/v1/api-key',
        method: 'POST',
        body: apiKey,
      }),
      invalidatesTags: ['ApiKey'],
      onQueryStarted: handleApiResult('Api_ApiKey_Add_Successful'),
    }),
    updateApiKey: builder.mutation<void, Partial<ApiKey> & Pick<ApiKey, '_id'>>({
      query: ({ _id, ...rest }) => ({
        url: `/v1/api-key/${_id}`,
        method: 'PATCH',
        body: rest,
      }),
      invalidatesTags: (_result, _error, { _id: id }) => [{ type: 'ApiKey', id }],
      onQueryStarted: handleApiResult('Api_ApiKey_Update_Successful'),
    }),
    deleteApiKey: builder.mutation<void, string>({
      query: (id) => ({
        url: `/v1/api-key/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, id) => [{ type: 'ApiKey', id }],
      onQueryStarted: handleApiResult('Api_ApiKey_Delete_Successful'),
    }),
  }),
});

export const {
  useGetUsersQuery,
  useAddUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useGetApiKeysQuery,
  useAddApiKeyMutation,
  useUpdateApiKeyMutation,
  useDeleteApiKeyMutation,
} = userApiSlice;
